import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldAlert, KeyRound, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Mandatory password-change screen.
 *
 * Shown immediately after login when the authenticated user has the
 * `must_change_password` flag set (e.g. the seeded `admin` account on a
 * fresh installation). The user cannot navigate anywhere else until the
 * default password has been rotated.
 */
const ChangePassword = ({ user, onLogout, onPasswordChanged }) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);

  const isForced = !!(user && user.must_change_password);

  const validate = () => {
    if (newPassword.length < 8) {
      return 'New password must be at least 8 characters long.';
    }
    const hasLetter = /[A-Za-z]/.test(newPassword);
    const hasDigit = /\d/.test(newPassword);
    if (!hasLetter || !hasDigit) {
      return 'New password must contain at least one letter and one digit.';
    }
    const tooCommon = ['password', 'admin123', '12345678', 'qwerty12', 'letmein1'];
    if (tooCommon.includes(newPassword.toLowerCase())) {
      return 'Password is too common — please choose a stronger one.';
    }
    if (newPassword === currentPassword) {
      return 'New password must be different from the current password.';
    }
    if (newPassword !== confirmPassword) {
      return 'The new password and confirmation do not match.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully. You can now use the platform.');
      // Update local user state so the flag is cleared in the SPA shell
      if (onPasswordChanged) {
        onPasswordChanged();
      }
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-4"
      data-testid="change-password-screen"
    >
      <div className="w-full max-w-lg">
        <div className="text-center mb-6">
          <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-3 ${isForced ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
            <ShieldAlert className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {isForced ? 'Set a new password' : 'Change your password'}
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
            {isForced
              ? 'For security reasons, this account is still using a default password and must be updated before you can continue.'
              : 'Update your password. Choose a strong one — at least 8 characters with letters and digits.'}
          </p>
        </div>

        <Card className="glass-card border-0 shadow-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="h-5 w-5 text-indigo-600" />
              <span>{isForced ? 'Mandatory password change' : 'Change password'}</span>
            </CardTitle>
            {user?.username && (
              <p className="text-xs text-slate-500 mt-1">
                Signed in as <span className="font-semibold">{user.username}</span>
              </p>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Current password */}
              <div className="space-y-2">
                <Label htmlFor="current-password">Current password</Label>
                <div className="relative">
                  <Input
                    id="current-password"
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    required
                    className="h-11 pr-12"
                    autoComplete="current-password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrent(!showCurrent)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    aria-label="Toggle current password visibility"
                  >
                    {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* New password */}
              <div className="space-y-2">
                <Label htmlFor="new-password">New password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="At least 8 characters, letters + digits"
                    required
                    className="h-11 pr-12"
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    aria-label="Toggle new password visibility"
                  >
                    {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {/* Confirm */}
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type={showNew ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter the new password"
                  required
                  className="h-11"
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>

              <ul className="text-xs text-slate-500 list-disc pl-5 pt-2 space-y-0.5">
                <li>Minimum 8 characters</li>
                <li>At least one letter and one digit</li>
                <li>Must differ from the current password</li>
              </ul>

              <Button
                type="submit"
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg"
                disabled={loading}
                data-testid="change-password-submit"
              >
                {loading ? 'Updating…' : (isForced ? 'Update password & continue' : 'Update password')}
              </Button>

              {isForced ? (
                <button
                  type="button"
                  onClick={() => {
                    if (onLogout) onLogout();
                    navigate('/login', { replace: true });
                  }}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1 pt-1"
                  data-testid="change-password-logout"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out instead
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center pt-1"
                  data-testid="change-password-cancel"
                >
                  Cancel
                </button>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;
